import { getFirestore, collection, query, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { UserService } from "../services/user.service.js";
import { DashboardService } from "../services/dashboard.service.js";
import { getAuth } from "firebase/auth";
import { SeriesEditorModal } from "../components/modals/SeriesEditorModal.js";

const db = getFirestore(app);
const auth = getAuth(app);

// Instantiate Modal Singleton (or per page)
const seriesEditor = new SeriesEditorModal();

export const AuthorStudioPage = async () => {
    // Main Container (Dark Theme)
    const container = document.createElement('div');
    container.className = 'min-h-screen bg-black text-gray-200 font-sans selection:bg-purple-500/30 pb-20';

    const initPage = async () => {
        // --- 1. Async Auth Guard ---
        const waitForAuth = new Promise((resolve) => {
            const unsubscribe = auth.onAuthStateChanged((user) => {
                unsubscribe();
                resolve(user);
            });
        });

        const userAuth = await waitForAuth;

        let userProfile = null;
        if (userAuth) {
            try {
                userProfile = await UserService.getUserProfile(userAuth.uid);
            } catch (e) {
                console.error("Failed to fetch user profile", e);
            }
        }

        const user = userProfile || userAuth;

        if (!user || !user.role || (user.role !== 'author' && user.role !== 'admin' && user.role !== 'super_admin')) {
            if (!userAuth) {
                window.router.navigate('/login');
            } else {
                await window.Modal.alert("작가 권한이 필요한 페이지입니다.");
                window.router.navigate('/');
            }
            return;
        }

        // --- 2. Render Content (Authorized) ---

        // Header Section
        const header = document.createElement('div');
        header.className = 'border-b border-zinc-800 bg-zinc-900/90 backdrop-blur-md sticky top-0 z-40';
        header.innerHTML = `
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
                <div class="flex items-center gap-3">
                     <button id="btn-toggle-drawer" class="text-zinc-400 hover:text-white transition-colors p-2 -ml-2 rounded-full hover:bg-zinc-800" title="${window.locale.t('studio.open_menu')}">
                        <span class="material-symbols-outlined">menu</span>
                    </button>
                    
                    <div class="h-5 w-px bg-zinc-700/50 mx-1 hidden lg:block"></div>

                    <button id="btn-exit-studio" class="text-zinc-400 hover:text-white transition-colors p-2 rounded-full hover:bg-zinc-800 flex items-center gap-1 group/exit" title="${window.locale.t('studio.exit')}">
                        <span class="material-symbols-outlined group-hover/exit:-translate-x-0.5 transition-transform">arrow_back</span>
                        <span class="text-xs font-bold hidden lg:inline pt-0.5">EXIT</span>
                    </button>
                    
                    <div class="flex items-center gap-3 select-none ml-2">
                        <span class="material-symbols-outlined text-purple-500 text-2xl">draw</span>
                        <h1 class="text-xl font-bold text-white tracking-tight">${window.locale.t('studio.title')}</h1>
                    </div>
                </div>
                <button id="btn-open-create-modal" class="group flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-5 py-2 rounded-xl transition-all shadow-lg shadow-purple-900/20 active:scale-[0.98]">
                    <span class="material-symbols-outlined group-hover:rotate-90 transition-transform text-[20px]">add</span>
                    <span class="font-bold text-sm hidden sm:inline">${window.locale.t('studio.new_series')}</span>
                     <span class="material-symbols-outlined sm:hidden">add</span>
                </button>
            </div>
        `;
        container.appendChild(header);

        // Bind Buttons
        header.querySelector('#btn-toggle-drawer').onclick = () => window.app?.drawer?.toggle();
        header.querySelector('#btn-exit-studio').onclick = () => window.router.navigate('/');

        // [MODAL TRIGGER] Open Create Modal
        header.querySelector('#btn-open-create-modal').onclick = () => {
            seriesEditor.open('create', null, null, () => refreshDashboard());
        };

        // Content Area
        const mainContent = document.createElement('main');
        mainContent.className = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8';
        container.appendChild(mainContent);

        // --- Loading State ---
        const loadingDiv = document.createElement('div');
        loadingDiv.className = 'flex justify-center py-20 text-purple-500';
        loadingDiv.innerHTML = '<span class="material-symbols-outlined animate-spin text-4xl">donut_large</span>';
        mainContent.appendChild(loadingDiv);

        // Placeholder Elements
        const kpiContainer = document.createElement('div');
        const chartContainer = document.createElement('div');
        const listContainer = document.createElement('div');

        mainContent.appendChild(kpiContainer);
        mainContent.appendChild(chartContainer);
        const experimentContainer = document.createElement('div');
        mainContent.appendChild(experimentContainer);
        experimentContainer.innerHTML = `
            <div class="bg-gradient-to-r from-zinc-900 to-zinc-800 border border-amber-500/30 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg shadow-amber-900/10 relative overflow-hidden group">
                <div class="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-black opacity-10 pointer-events-none transition-all duration-700"></div>
                <div class="relative z-10">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-amber-500/20 text-amber-500 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-500/30">${window.locale.t('studio.lab_badge')}</span>
                        <h3 class="text-xl font-bold text-white flex items-center gap-2">
                             ${window.locale.t('studio.lab_title')}
                        </h3>
                    </div>
                    <p class="text-zinc-400 text-sm max-w-xl">
                        ${window.locale.t('studio.lab_desc')}
                    </p>
                </div>

            </div>
        `;

        mainContent.appendChild(listContainer);

        // --- Dynamic Refresh Function ---
        const refreshDashboard = async () => {
            try {
                const stats = await DashboardService.getAuthorStats(user.uid);

                if (loadingDiv.parentElement) loadingDiv.remove();

                // 1. KPI Cards
                const kpiCard = (title, value, icon, colorClass) => `
                    <div class="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex items-center justify-between hover:border-zinc-700 transition-colors">
                        <div>
                            <h3 class="text-zinc-500 text-sm font-bold uppercase tracking-wider mb-1">${title}</h3>
                            <p class="text-3xl font-black text-white ${colorClass}">${value.toLocaleString()}</p>
                        </div>
                        <div class="w-12 h-12 rounded-full ${colorClass.replace('text-', 'bg-').replace('500', '500/20')} flex items-center justify-center">
                            <span class="material-symbols-outlined text-2xl ${colorClass}">${icon}</span>
                        </div>
                    </div>
                `;
                kpiContainer.className = 'grid grid-cols-1 md:grid-cols-3 gap-4';
                kpiContainer.innerHTML = `
                    ${kpiCard(window.locale.t('studio.total_views'), stats.summary.totalViews, 'visibility', 'text-blue-500')}
                    ${kpiCard(window.locale.t('studio.total_likes'), stats.summary.totalLikes, 'favorite', 'text-red-500')}
                    ${kpiCard(window.locale.t('studio.total_followers'), stats.summary.totalFollowers, 'group', 'text-purple-500')}
                `;

                // 2. Chart
                chartContainer.innerHTML = '';
                const chartWrapper = document.createElement('div');
                chartWrapper.className = 'bg-zinc-900 border border-zinc-800 rounded-2xl p-6 relative overflow-hidden';
                chartWrapper.innerHTML = `
                    <div class="flex justify-between items-center mb-6">
                        <div>
                            <h3 class="text-lg font-bold text-white">${window.locale.t('studio.weekly_performance')}</h3>
                            <p class="text-xs text-zinc-500">${window.locale.t('studio.views_trend_7d')}</p>
                        </div>
                         <div class="flex items-center gap-2">
                             <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                             <span class="text-xs text-zinc-400">${window.locale.t('studio.live_data')}</span>
                        </div>
                    </div>
                    <div class="relative h-64 w-full">
                        <canvas id="studio-analytics-chart"></canvas>
                    </div>
                `;
                chartContainer.appendChild(chartWrapper);
                setTimeout(() => {
                    if (window.Chart) {
                        const ctx = chartWrapper.querySelector('#studio-analytics-chart').getContext('2d');
                        new Chart(ctx, {
                            type: 'line',
                            data: stats.chartData,
                            options: {
                                responsive: true, maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                                scales: {
                                    y: { beginAtZero: true, grid: { color: 'rgba(255, 255, 255, 0.1)' }, ticks: { color: '#a1a1aa' } },
                                    x: { grid: { display: false }, ticks: { color: '#a1a1aa' } }
                                }
                            }
                        });
                    }
                }, 0);

                // 3. Series List
                listContainer.innerHTML = '';
                const seriesSection = document.createElement('div');
                seriesSection.className = 'space-y-4';
                const seriesHeader = document.createElement('div');
                seriesHeader.className = 'flex justify-between items-end pb-2 border-b border-zinc-800';
                seriesHeader.innerHTML = `<div><h3 class="text-lg font-bold text-white">${window.locale.t('studio.my_series_list')}</h3><p class="text-xs text-zinc-500">${window.locale.t('studio.total_series_count', { n: stats.summary.totalSeries })}</p></div>`;
                seriesSection.appendChild(seriesHeader);

                const gridContainer = document.createElement('div');
                gridContainer.className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6';

                if (stats.seriesList.length === 0) {
                    gridContainer.innerHTML = `
                        <div class="col-span-full flex flex-col items-center justify-center py-20 text-zinc-500 border-2 border-dashed border-zinc-800 rounded-3xl bg-zinc-900/30">
                            <span class="material-symbols-outlined text-6xl mb-4 opacity-50">library_add</span>
                            <p class="text-lg font-medium">${window.locale.t('studio.empty_series_title')}</p>
                            <p class="text-sm mt-2 opacity-60">${window.locale.t('studio.empty_series_desc')}</p>
                        </div>
                    `;
                } else {
                    stats.seriesList.forEach(series => {
                        let typeLabel = window.locale.t('common.webnovel') || "Web Novel", typeIcon = "article", typeColor = "bg-blue-500/20 text-blue-300 border-blue-500/30";
                        if (series.type === 'webtoon') {
                            typeLabel = window.locale.t('common.webtoon') || "Webtoon"; typeIcon = "image"; typeColor = "bg-pink-500/20 text-pink-300 border-pink-500/30";
                        } else if (series.type === 'davinci') {
                            typeLabel = window.locale.t('common.cinenovel') || "Cine-Novel"; typeIcon = "movie"; typeColor = "bg-rose-500/20 text-rose-300 border-rose-500/30";
                        }
                        const card = document.createElement('div');
                        card.className = "group bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-900/10 transition-all duration-300 flex flex-col relative";

                        // [FIX] Checking badges
                        const isAdult = series.isAdult;
                        const isAI = series.isAI;

                        card.innerHTML = `
                            <div class="aspect-[2/3] w-full bg-zinc-950 relative overflow-hidden">
                                ${series.coverImage
                                ? `<img src="${series.coverImage}" class="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700">`
                                : `<div class="w-full h-full flex flex-col items-center justify-center text-zinc-700">
                                         <span class="material-symbols-outlined text-6xl mb-2 opacity-50">image_not_supported</span><span class="text-xs font-mono">No Cover</span>
                                       </div>`
                            }
                                <div class="absolute top-3 left-3 px-2.5 py-1 rounded-lg text-xs font-bold border ${typeColor} backdrop-blur-md flex items-center gap-1.5 shadow-lg">
                                    <span class="material-symbols-outlined text-[14px]">${typeIcon}</span> ${typeLabel}
                                </div>
                                <div class="absolute top-3 right-3 px-2 py-1 rounded-md text-[10px] font-bold bg-black/60 text-white border border-white/10 backdrop-blur-md uppercase">
                                    ${series.status}
                                </div>
                                <!-- 19+ Badge -->
                                ${isAdult ? '<div class="absolute bottom-3 right-3 bg-red-600 text-white text-xs font-black px-2 py-0.5 rounded shadow-lg animate-pulse" title="19세 이상 이용가">19</div>' : ''}
                                <!-- AI Badge -->
                                ${isAI ? '<div class="absolute bottom-3 left-3 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded border border-white/20" title="AI 생성 콘텐츠">AI</div>' : ''}
                            </div>
                            <div class="p-5 flex flex-col flex-1 h-full relative">
                                <div class="relative z-0 overflow-hidden mb-4 h-[72px] pointer-events-none select-none">
                                    <h3 class="font-bold text-lg text-white mb-1 line-clamp-1 group-hover:text-purple-400 transition-colors pointer-events-none">${series.title}</h3>
                                    <p class="text-sm text-zinc-400 leading-relaxed line-clamp-2 pointer-events-none">
                                        ${series.description || 'No description available.'}
                                    </p>
                                </div>
                                 <div class="flex items-center gap-4 mb-4 text-xs text-zinc-500 border-t border-zinc-800 pt-3">
                                    <div class="flex items-center gap-1"><span class="material-symbols-outlined text-[14px]">visibility</span> ${(series.stats?.viewCount || 0).toLocaleString()}</div>
                                    <div class="flex items-center gap-1"><span class="material-symbols-outlined text-[14px]">favorite</span> ${(series.stats?.likeCount || 0).toLocaleString()}</div>
                                </div>
                                <div class="mt-auto relative w-full space-y-3 pointer-events-auto bg-zinc-900 pt-2 flex-shrink-0 z-10">
                                    <div class="flex gap-2">
                                        <button class="btn-builder flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-colors border border-zinc-700 group-hover:border-zinc-600 flex items-center justify-center gap-2 group/btn relative">
                                            <span class="material-symbols-outlined text-[18px] text-purple-400 group-hover/btn:text-purple-300 transition-colors pointer-events-none">edit_square</span>
                                            ${window.locale.t('studio.btn_editor')}
                                        </button>
                                        <button class="btn-settings flex-1 py-2.5 bg-transparent hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl text-xs font-bold transition-colors border border-zinc-800 hover:border-zinc-600 relative">
                                            ${window.locale.t('studio.btn_settings')}
                                        </button>
                                        <button class="btn-share w-10 h-10 flex-shrink-0 flex items-center justify-center bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 hover:text-purple-300 rounded-xl transition-colors border border-purple-500/20 hover:border-purple-500/40 relative" title="작품 홍보">
                                            <span class="material-symbols-outlined text-[18px] pointer-events-none">share</span>
                                        </button>
                                    </div>
                                    <button class="btn-delete w-full py-2 bg-red-900/20 hover:bg-red-900/40 text-red-500 rounded-xl text-xs font-bold border border-red-900/30 hover:border-red-500/50 transition-colors flex items-center justify-center gap-1 relative">
                                         <span class="material-symbols-outlined text-[16px] pointer-events-none">delete</span> ${window.locale.t('studio.btn_delete')}
                                    </button>
                                </div>
                            </div>
                        `;
                        const ensureClick = (e, callback) => { e.stopPropagation(); callback(); };

                        // [MODAL TRIGGER] Edit Mode
                        card.querySelector('.btn-builder').addEventListener('click', (e) => ensureClick(e, () => window.router.navigate(`/studio/series/${series.id}/editor`)));
                        card.querySelector('.btn-settings').addEventListener('click', (e) => ensureClick(e, () => {
                            seriesEditor.open('edit', series, series.id, () => refreshDashboard());
                        }));
                        card.querySelector('.btn-share').addEventListener('click', (e) => ensureClick(e, () => {
                            const shareType = series.type === 'webtoon' ? 'webtoon' : (series.type === 'davinci' ? 'webtoon' : 'novel');
                            const shareUrl = `https://ssulwha.world/share/${shareType}/${series.id}`;
                            window.Modal.showSharePrompt(shareUrl, series.title);
                        }));
                        card.querySelector('.btn-delete').addEventListener('click', (e) => ensureClick(e, () => openDeleteModal(series.id, series.title)));

                        gridContainer.appendChild(card);
                    });
                }
                seriesSection.appendChild(gridContainer);
                listContainer.appendChild(seriesSection);

            } catch (err) {
                console.error(err);
                if (loadingDiv.parentElement) loadingDiv.innerHTML = `<div class="text-center text-red-500">Failed to load: ${err.message}</div>`;
            }
        };

        refreshDashboard();

        // --- Delete Modal (Minimal, specific to AuthorStudio) ---
        // Can be refactored later but keeping here for now as it's simple
        const deleteModalHTML = `
        <div id="delete-series-modal" class="fixed inset-0 z-[9999] hidden flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in opacity-0 transition-opacity duration-300 isolate" style="z-index: 9999;">
            <div class="bg-zinc-900 border border-red-900/50 rounded-2xl p-6 w-full max-w-sm shadow-2xl transform scale-95 transition-transform duration-300" id="delete-modal-box">
                <div class="flex flex-col items-center text-center">
                    <span class="material-symbols-outlined text-5xl text-red-500 mb-4 bg-red-500/10 p-4 rounded-full">delete_forever</span>
                    <h3 class="text-white font-bold text-xl mb-2">${window.locale.t('studio.delete_modal_title')}</h3>
                    <p class="text-zinc-400 text-sm mb-6 leading-relaxed">
                        ${window.locale.t('studio.delete_modal_content', { title: '<span id="delete-target-title" class="text-white font-bold"></span>' })}
                    </p>
                    <div class="flex gap-3 w-full">
                        <button id="btn-cancel-delete" class="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold rounded-xl transition-colors">${window.locale.t('common.cancel')}</button>
                        <button id="btn-confirm-delete" class="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98]">${window.locale.t('common.delete')}</button>
                    </div>
                </div>
            </div>
        </div>
        `;
        container.insertAdjacentHTML('beforeend', deleteModalHTML);

        const deleteModal = container.querySelector('#delete-series-modal');
        const deleteModalBox = container.querySelector('#delete-modal-box');
        const deleteTitle = container.querySelector('#delete-target-title');
        let deleteTargetId = null;

        const openDeleteModal = (id, title) => {
            deleteTargetId = id;
            deleteTitle.textContent = title;
            deleteModal.classList.remove('hidden');
            setTimeout(() => { deleteModal.classList.remove('opacity-0'); deleteModalBox.classList.remove('scale-95'); deleteModalBox.classList.add('scale-100'); }, 10);
        };
        const closeDeleteModal = () => {
            deleteModal.classList.add('opacity-0'); deleteModalBox.classList.remove('scale-100'); deleteModalBox.classList.add('scale-95');
            setTimeout(() => { deleteModal.classList.add('hidden'); deleteTargetId = null; }, 300);
        };

        container.querySelector('#btn-cancel-delete').onclick = closeDeleteModal;
        const btnConfirmDelete = container.querySelector('#btn-confirm-delete');
        btnConfirmDelete.onclick = async () => {
            if (!deleteTargetId) return;
            btnConfirmDelete.textContent = "삭제 중...";
            try {
                await deleteDoc(doc(db, "series", deleteTargetId));
                closeDeleteModal();
                refreshDashboard();
            } catch (e) {
                await window.Modal.alert("Error: " + e.message);
            } finally {
                btnConfirmDelete.textContent = "삭제하기";
            }
        };

    };

    initPage();
    return container;
};
